import React, { useState, useEffect } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { useConversations } from '../../contexts/ConversationContext';
import { useAuth } from '../../contexts/AuthContext';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ConversationSidebar from './ConversationSidebar';
import './ChatWidget.css';

const ChatWidget: React.FC = () => {
  const { user } = useAuth();
  const { messages, sendMessage, isLoading, loadingStatus, activeFunctionLabel, clearChat, sessionId, loadConversationMessages } = useChat();
  const { 
    selectConversation, 
    createNewConversation, 
    refreshCurrentConversation
  } = useConversations();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);

  const handleSendMessage = async (content: string) => {
    const currentSessionId = await sendMessage(content);
    
    // Refresh current conversation after sending message (updates sidebar immediately)
    if (currentSessionId) {
      await refreshCurrentConversation(currentSessionId);
    }
  };

  const handleSelectConversation = async (sessionId: string) => {
    await selectConversation(sessionId);
    // Load the conversation messages into chat
    await loadConversationMessages(sessionId);
    setSidebarOpen(window.innerWidth >= 1024); // Close sidebar on mobile after selection
  };

  const handleNewConversation = async () => {
    if (messages.length > 0) {
      try {
        await createNewConversation();
        clearChat();
      } catch (error) {
        console.error('Failed to create new conversation:', error);
      }
    }
  };

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="chat-layout">
      {/* Conversation Sidebar */}
      <ConversationSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentSessionId={sessionId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        hasMessages={messages.length > 0}
      />

      {/* Main Chat Area */}
      <main className={`chat-main ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="sidebar-open-btn"
            title="Abrir conversaciones"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18m-9-9l9 9-9 9"></path>
            </svg>
          </button>
        )}

        <div className="chat-main-inner">
          <MessageList
            messages={messages}
            isLoading={isLoading}
            loadingStatus={loadingStatus}
            activeFunctionLabel={activeFunctionLabel}
            showFunctionDetails={Boolean(user?.is_admin)}
          />
          <MessageInput onSendMessage={handleSendMessage} disabled={isLoading} />
        </div>
      </main>
    </div>
  );
};

export default ChatWidget;
